import type { SearchClient } from 'algoliasearch';
import dayjs from 'dayjs';
import type { AlgoliaHit, SearchOptions } from 'instantsearch.js';
import type { ParsedUrlQuery } from 'querystring';
import type { InstantSearchServerState } from 'react-instantsearch';
import aa from 'search-insights';

import type { Country, Locale } from '@/types/regionTypes';
import { getIntParam } from '@/utils/urlUtils';

import type { CountryCode, LanguageCodeEnum } from '@/graphql/saleor/api';
import { algoliaSearchClient } from '@/integrations/algolia/client';
import { ALGOLIA_EVENT_NAME } from '@/integrations/algolia/const';
import { algoliaSsrSearchClient } from '@/integrations/algolia/ssrClient';
import type { ProductHit } from '@/integrations/algolia/types';
import { AlgoliaEventsNames } from '@/integrations/algolia/types';
import { ENVIRONMENT_NAME, NEXT_PUBLIC_DEFAULT_APP_LOCALE } from '@/lib/const';
import { paths } from '@/lib/paths';
import { getRegionFromLocale } from '@/lib/regions';
import { getStrapMaterial, supportedStrapMaterials } from '@/lib/straps';

import type {
  ALGOLIA_INDEX_DATA_TYPE,
  AlgoliaEvent,
  EyewearHit,
  SearchHit,
  SearchUiState,
  StrapHit,
  WatchHit,
} from './types';

export type ProductType = 'Watch' | 'Strap' | 'Store' | 'Eyewear';
export type SearchUiStateWithLocale = SearchUiState & { locale?: Locale };
export const generateWatchDetailPagePath = ({
  collections,
  model,
  objectID,
}: Pick<WatchHit, 'collections' | 'model' | 'objectID'>) => {
  if (collections?.length === 0) {
    return '#';
  }
  return paths.watches.collection.model.product.asPath({
    model: model?.slug,
    collection: collections?.[0]?.slug?.toLowerCase(),
    sku: objectID,
  });
};

export const generateStrapDetailPagePath = ({
  material_en,
  objectID,
}: StrapHit) => {
  const productMaterial = getStrapMaterial(material_en);

  if (!productMaterial) {
    throw new Error(
      `Strap with material: ${material_en} is not supported and cannot be displayed`,
    );
  }

  return paths.straps.material.product.asPath({
    material: productMaterial,
    sku: objectID,
  });
};

export const normalizeAccentedChars = (text: string): string => {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const generateEyewearDetailPagePath = ({
  name_en,
  objectID,
}: Pick<EyewearHit, 'name_en' | 'objectID'>) => {
  return paths.sunglasses.product.asPath({
    modelSku: normalizeAccentedChars(
      `${name_en?.toLowerCase().replace(' ', '-')}-${objectID}`,
    ),
  });
};

const matchesField = (
  fieldName: string,
  key: string,
  exclude?: string,
): boolean => {
  const baseRegex = new RegExp(`^${fieldName}(\\.|$)`); // Match the exact field name followed by "." or end of string
  const excludeRegex = exclude ? new RegExp(exclude) : null; // Optional regex for excluding unwanted patterns
  return baseRegex.test(key) && (!excludeRegex || !excludeRegex.test(key));
};

export const buildQueryParamsFromSearchUiState = (
  uiState: SearchUiState,
  indexName?: string,
  query?: ParsedUrlQuery,
  serverState?: InstantSearchServerState,
): Record<string, string> => {
  const queryParams = new Map();

  const indexId = Object.keys(uiState)[0];
  const searchState = indexName ? uiState[indexName] : uiState[indexId];

  const searchStateByServerState = indexName
    ? serverState?.initialResults[indexName]
    : serverState?.initialResults[indexId];
  // Get the key from Algolia that supports the query If not then use the alternative
  const searchableAttributesByServerState =
    searchStateByServerState?.results?.[0].renderingContent?.facetOrdering
      ?.facets?.order;
  const searchableAttributes = searchableAttributesByServerState ?? [
    'collection',
    'type',
    'type_en',
    'size',
    'case',
    'dial_color',
    'lens_color',
    'lens_color_en',
    'frame_color',
    'frame_shape_en',
    'frame_material',
    'distance_between_horns',
    'material',
    'color',
    'strap_length',
    'buckle_type',
  ];

  searchableAttributes?.forEach((field: string) => {
    if (searchState?.refinementList) {
      const matchedKey = Object.keys(searchState?.refinementList)?.find(
        (key: string) => {
          // Match logic for specific fields
          switch (field) {
            case 'dial_color':
              // Match keys starting with "dial_color" but exclude those containing "color"
              return matchesField('dial_color', key, 'color');
            // case 'color':
            //   // Match keys starting with "color" but exclude those containing "dial_color"
            //   return matchesField('color', key, 'dial_color');
            case 'lens_color':
              // Match keys starting with "lens_color" specifically
              return matchesField('lens_color', key);
            case 'frame_color':
              // Match keys starting with "frame_color" specifically
              return matchesField('frame_color', key);
            case 'frame_material':
              return matchesField('frame_material', key);
            case 'size':
              // Match keys starting with "size" but exclude those containing "lug_size"
              return matchesField('size', key, 'lug_size');
            case 'lug_size':
              return matchesField('lug_size', key, 'size');
            default:
              // Default case: Match keys starting with the field name
              return matchesField(field, key);
          }
        },
      );
      if (matchedKey) {
        queryParams.set(field.replace(/\..*?$/, ''), [
          ...searchState.refinementList[
            matchedKey as keyof typeof searchState.refinementList
          ],
        ]);
      }
    }
  });

  if (searchState?.refinementList?.collection) {
    queryParams.set('collection', searchState.refinementList.collection);
  }
  if (searchState?.refinementList?.distance_between_horns_en) {
    queryParams.set(
      'distance_between_horns_en',
      searchState.refinementList.distance_between_horns_en,
    );
  }
  if (searchState?.refinementList?.objectID) {
    queryParams.set(
      'objectID',
      searchState.refinementList.objectID.map((string) =>
        string.trim().toUpperCase(),
      ),
    );
  }

  // Also handle eyewear-specific refinements
  if (searchState?.refinementList?.type_en) {
    queryParams.set('type_en', searchState.refinementList.type_en);
  }
  if (searchState?.refinementList?.lens_color_en) {
    queryParams.set('lens_color_en', searchState.refinementList.lens_color_en);
  }
  // Handle the new lens_color attribute with region code
  const lensColorKey = Object.keys(searchState?.refinementList || {}).find(
    (key) => key.startsWith('lens_color.') || key === 'lens_color',
  );
  if (lensColorKey && searchState?.refinementList?.[lensColorKey]) {
    queryParams.set('lens_color', searchState.refinementList[lensColorKey]);
  }
  // Handle the frame_color attribute with region code
  const frameColorKey = Object.keys(searchState?.refinementList || {}).find(
    (key) => key.startsWith('frame_color.') || key === 'frame_color',
  );
  if (frameColorKey && searchState?.refinementList?.[frameColorKey]) {
    queryParams.set('frame_color', searchState.refinementList[frameColorKey]);
  }
  if (searchState?.refinementList?.frame_shape_en) {
    queryParams.set(
      'frame_shape_en',
      searchState.refinementList.frame_shape_en,
    );
  }

  if (searchState?.range?.price) {
    const price = searchState.range.price;
    const priceParts = price?.split(':');

    if (priceParts[0]) {
      queryParams.set('priceFrom', priceParts[0]);
    }

    if (priceParts[1]) {
      queryParams.set('priceTo', priceParts[1]);
    }
  }

  if (searchState?.sortBy) {
    if (searchState.sortBy.endsWith('price_asc')) {
      queryParams.set('sort-by', 'price_asc');
    }
    if (searchState.sortBy.endsWith('price_desc')) {
      queryParams.set('sort-by', 'price_desc');
    }
  }

  const page = searchState?.page;
  const pageNumber = page ?? 1;
  if (pageNumber > 1) {
    queryParams.set('page', pageNumber);
  }

  if (searchState?.query) {
    let query = searchState.query;
    if (Array.isArray(query)) {
      query = query.join(',');
    }
    if (query.trim() !== '') {
      queryParams.set('q', query);
    }
  }

  const mutableSearchableAttributes = [...searchableAttributes];
  const newQuery = removedSearchableQueryParamsFromCurrentQuery(
    mutableSearchableAttributes,
    query,
  );

  return {
    ...newQuery,
    ...Object.fromEntries(queryParams),
  };
};

// This allow us to return the query params that are not from the filters (queries for trackign for instance)
export const removedSearchableQueryParamsFromCurrentQuery = (
  searchableAttributes: string[],
  query?: ParsedUrlQuery,
) => {
  if (!query) return {};

  const newQuery = { ...query };
  searchableAttributes.forEach((attribute) => {
    delete newQuery[attribute.split('.')[0]];
  });
  delete newQuery['collection'];
  delete newQuery['distance_between_horns_en'];
  delete newQuery['objectID'];
  delete newQuery['priceFrom'];
  delete newQuery['priceTo'];
  delete newQuery['sort-by'];
  delete newQuery['page'];
  delete newQuery['q'];
  // Remove eyewear attributes
  delete newQuery['type_en'];
  delete newQuery['lens_color_en'];
  delete newQuery['lens_color'];
  delete newQuery['frame_color'];
  delete newQuery['frame_shape_en'];

  return newQuery;
};

export const buildUiStateFromURL = (
  path: string,
  initIndexName: string,
  currentIndexName?: string,
  shortIndex?: string,
  locale?: Locale,
): SearchUiState => {
  const routeState = parseUrlToRouteState(path);

  return buildUiStateFromRouteState(
    routeState,
    initIndexName,
    currentIndexName,
    shortIndex,
    locale,
  );
};

export const buildUiStateFromRouteState = (
  routeState: Record<string, any>,
  initIndexName: string,
  currentIndexName?: string,
  shortIndex?: string,
  locale?: Locale,
): SearchUiState => {
  const regionCode = locale?.code?.toUpperCase() || 'EN_US';

  const refinementList = {
    collection: routeState['collection'] ?? [],
    case: routeState['case'] ?? [],
    dial_color: routeState['dial_color'] ?? [],
    size: routeState['size'] ?? [],
    type: routeState['type'] ?? [],
    type_en: routeState['type_en'] ?? [],
    lens_color: routeState['lens_color'] ?? [],
    frame_color: routeState['frame_color'] ?? [],
    frame_material: routeState['frame_material'] ?? [],
    lens_color_en: routeState['lens_color_en'] ?? [],
    frame_shape_en: routeState['frame_shape_en'] ?? [],
    distance_between_horns_en: routeState['distance_between_horns_en'] ?? [],
    material: routeState['material'] ?? [],
    color: routeState['color'] ?? [],
    strap_length: routeState['strap_length'] ?? [],
    buckle_type: routeState['buckle_type'] ?? [],
    objectID: routeState['objectID'] ?? [],
    lug_type: routeState['lug_type'] ?? [],
  };
  const updatedRefinementList: Record<string, any> = Object.entries(
    refinementList,
  ).reduce((acc: Record<string, any>, [key, value]) => {
    if (
      [
        'size',
        'collection',
        'distance_between_horns_en',
        'type_en',
        'lens_color_en',
        'frame_shape_en',
      ]?.includes(key)
    ) {
      acc[key] = value;
    } else if (key === 'objectID') {
      acc[key] = value.map((string: string) => string.trim().toUpperCase());
    } else {
      const updatedKey = `${key}.${regionCode}`;
      acc[updatedKey] = value;
    }
    return acc;
  }, {});

  const formatPrice = (price: any) =>
    isNaN(Number(price)) ? '' : String(Number(price));
  const priceFrom = formatPrice(routeState['priceFrom']);
  const priceTo = formatPrice(routeState['priceTo']);

  const range = { price: `${priceFrom}:${priceTo}` };

  let sortBy = '';
  const sortByParam =
    typeof routeState['sort-by'] === 'string'
      ? routeState['sort-by']
      : routeState['sort-by']?.[0];

  if (sortByParam) {
    // sort need test
    if (['price_asc', 'price_desc']?.includes(sortByParam)) {
      sortBy = `${currentIndexName ?? initIndexName}.${routeState['sort-by']}`;
    }
  }

  const page = getIntParam(routeState['page']);

  const state = {
    refinementList: updatedRefinementList,
    range,
    sortBy,
    page,
    query: routeState['q'] ?? '',
  };

  return {
    [initIndexName]: state,
    ...(shortIndex ? { [shortIndex]: state } : undefined),
  };
};
export const getIndexNameByCountry = (
  type: ALGOLIA_INDEX_DATA_TYPE,
  ctxLocale: string,
  countryFromPDP: Country,
): string => {
  const { country } = getRegionFromLocale(ctxLocale);
  const rightCountry = countryFromPDP ?? country;
  if (type === 'STORE') {
    return `${ENVIRONMENT_NAME}.${type}`;
  } else if (type === 'SERVICE_CENTER') {
    return `${ENVIRONMENT_NAME}.SERVICE_CENTER`;
  }
  return `${ENVIRONMENT_NAME}.${type}.${rightCountry.channelSlug}`;
};

export const getIndexName = (
  type: ALGOLIA_INDEX_DATA_TYPE,
  ctxLocale: string,
): string => {
  const { country } = getRegionFromLocale(ctxLocale);
  if (type === 'STORE') {
    return `${ENVIRONMENT_NAME}.STORE`;
  } else if (type === 'SERVICE_CENTER') {
    return `${ENVIRONMENT_NAME}.SERVICE_CENTER`;
  }
  return `${ENVIRONMENT_NAME}.PRODUCT.${country.channelSlug}`;
};

export const isWatchHit = (
  b?: WatchHit | StrapHit | EyewearHit,
): b is WatchHit => {
  return b?.product_type === 'Watch';
};

export const isStrapHit = (
  b?: WatchHit | StrapHit | EyewearHit,
): b is StrapHit => {
  return b?.product_type === 'Strap';
};

export const isEyewearHit = (
  b?: WatchHit | StrapHit | EyewearHit,
): b is EyewearHit => {
  return b?.product_type === 'Eyewear';
};

export const onlySupportedStrapMaterialsFilter = supportedStrapMaterials
  .map((material) => `material:'${material}'`)
  .join(' OR ');

export const performMultiSearch = async <TObj = SearchHit>(
  query: string,
  locale: string,
  params?: SearchOptions,
  draftMode?: boolean,
  code?: CountryCode,
  queryType?: 'prefixLast' | 'prefixAll' | 'prefixNone' | undefined,
  typoTolerance?: boolean | 'min' | 'strict' | undefined,
) =>
  algoliaSearchClient.multipleQueries<TObj>([
    {
      indexName: getIndexName('PRODUCT', locale),
      query,
      params: {
        ...params,
        filters:
          params?.filters ??
          applyProductConfigurationFilters(
            ProductConfigurationFilterType.SEARCH,
            'Watch',
            draftMode,
            '',
          ),
        typoTolerance: typoTolerance,
        queryType: queryType ?? 'prefixLast',
      },
    },
    {
      indexName: getIndexName('PRODUCT', locale),
      query,
      params: {
        ...params,
        filters:
          params?.filters ??
          applyProductConfigurationFilters(
            ProductConfigurationFilterType.SEARCH,
            'Strap',
            draftMode,
            onlySupportedStrapMaterialsFilter,
          ),
      },
    },
    {
      indexName: getIndexName('STORE', locale),
      query,
      params: {
        filters: `web_visible:true`,
      },
    },
  ]);

export const TypeIndexId: Record<ALGOLIA_INDEX_DATA_TYPE, string> = {
  PRODUCT: 'products',
  STORE: 'stores',
  SERVICE_CENTER: 'SERVICE_CENTER',
};

export enum ProductConfigurationFilterType {
  PLP = 'plp',
  PDP = 'pdp',
  SEARCH = 'search',
  COMPONENTS = 'components',
  WIDGET = 'widget',
}

export function getWatchFilters(
  query: string | string[] | undefined,
  defaultFilters: string,
): [string, boolean[]] {
  if (!query) {
    return [defaultFilters, [false]];
  }

  const pattern = /[A-Z]{1,2}\d{4,8}[A-Z]{1}\d{1}[A-Z]{1}\d{1}/i;
  if (Array.isArray(query)) {
    const filters = [];
    const isWatchSkuArray = [];
    for (const q of query) {
      const isWatchSku = pattern.test(q);
      if (isWatchSku) {
        filters.push(`objectID: ${q}`);
      }
      isWatchSkuArray.push(isWatchSku);
    }
    const filterString =
      filters.length > 0 ? `${defaultFilters}` : defaultFilters;

    return [filterString, isWatchSkuArray];
  } else {
    const isWatchSku = pattern.test(query);
    const filterString = isWatchSku ? `${defaultFilters}` : defaultFilters;
    return [filterString, [isWatchSku]];
  }
}

/**
 * Checks if a query matches an eyewear SKU pattern and creates filter based on that
 * Example eyewear SKUs: JZ310615.25, JZ310615_25
 * Pattern: Two letters followed by 6 numbers, then a dot or underscore, then 2 numbers
 */
export function getEyewearFilters(
  query: string | string[] | undefined,
  defaultFilters: string,
): [string, boolean[]] {
  if (!query) {
    return [defaultFilters, [false]];
  }

  const pattern = /^[A-Z]{2}\d{6}[._]\d{2}$/i;
  if (Array.isArray(query)) {
    const filters = [];
    const isEyewearSkuArray = [];
    for (const q of query) {
      const isEyewearSku = pattern.test(q);
      if (isEyewearSku) {
        filters.push(`objectID: ${q}`);
      }
      isEyewearSkuArray.push(isEyewearSku);
    }
    const filterString =
      filters.length > 0 ? `${defaultFilters}` : defaultFilters;

    return [filterString, isEyewearSkuArray];
  } else {
    const isEyewearSku = pattern.test(query);
    const filterString = isEyewearSku ? `${defaultFilters}` : defaultFilters;
    return [filterString, [isEyewearSku]];
  }
}

export const applyProductConfigurationFilters = (
  type: ProductConfigurationFilterType,
  productType?: ProductType,
  draftMode: boolean | undefined = false,
  originalFilters = '',
  zeroTime: boolean | undefined = false,
) => {
  if (productType === 'Store') {
    return originalFilters;
  }
  const currentUnixTimestamp = zeroTime
    ? Math.floor(dayjs().utc().startOf('date').toDate().getTime() / 1000)
    : Math.floor(new Date().getTime() / 1000);

  const useProductConfigurator =
    process.env.NEXT_PUBLIC_USE_PRODUCT_CONFIGURATOR === 'true';

  let productConfigurationFilters = originalFilters;

  if (productType != null) {
    productConfigurationFilters =
      originalFilters && originalFilters.length > 0
        ? `product_type:${productType} AND ${originalFilters}`
        : `product_type:${productType}`;
  }

  if (useProductConfigurator) {
    const releaseFilter = `${type}_release_ts < ${currentUnixTimestamp} AND ${type}_expire_ts > ${currentUnixTimestamp}`;
    const previewFilter = `${type}_preview=1`;

    if (
      productConfigurationFilters &&
      productConfigurationFilters.trim() !== ''
    ) {
      if (!draftMode) {
        productConfigurationFilters += ` AND ${releaseFilter}`;
      }

      if (draftMode) {
        productConfigurationFilters += ` AND ${previewFilter}`;
      }
    } else {
      if (!draftMode) {
        productConfigurationFilters = releaseFilter;
      }
      if (draftMode) {
        productConfigurationFilters = previewFilter;
      }
    }
  }

  return productConfigurationFilters.replace(
    'objectID:objectID:',
    'objectID:',
  ) as string;
};

export const filterHits = (results: any, draftMode: boolean) => {
  if (results?.results === undefined) {
    return [];
  }
  return results?.results.filter((hit: any) => {
    if (!hit) return false;

    const currentUnixTimestamp = Math.floor(new Date().getTime() / 1000);
    const releaseKey = 'components_release_ts';
    const expireKey = 'components_expire_ts';
    const previewKey = 'components_preview';
    const isPublished =
      hit[releaseKey] < currentUnixTimestamp &&
      hit[expireKey] > currentUnixTimestamp;

    const isPreview = hit[previewKey];

    const isWithinTimeRange = draftMode
      ? isPublished || isPreview
      : isPublished;

    return isWithinTimeRange;
  });
};

const createSkuWithProductConfigFilter = (
  productType: ProductType,
  sku: string,
  draftMode: boolean,
) =>
  applyProductConfigurationFilters(
    ProductConfigurationFilterType.PDP,
    productType,
    draftMode,
    `objectID:${sku}`,
  );

export const filterEnabledStraps = async (
  country: Country,
  languageCode: LanguageCodeEnum,
  bundleVariationSkus: string[],
  draftMode = false,
) => {
  const indexName = getIndexNameByCountry(
    'PRODUCT',
    languageCode ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
    country,
  );

  const index = algoliaSearchClient.initIndex(indexName);

  /*
  Perform multiple searches as doing a single search with OR between SKUs was sometimes not returning correct data,
  example: sku:A17326361L1P2 OR sku:A17326361L1A1 => only one object with sku matching A17326361L1P2 is returned.
  To be investigated further.
  */
  try {
    const searchResults = await Promise.all(
      bundleVariationSkus.map((sku) => {
        return index.search('', {
          filters: createSkuWithProductConfigFilter('Watch', sku, draftMode),
        });
      }),
    );

    return searchResults
      .map((result) => {
        const hit = result.hits.length ? (result.hits[0] as WatchHit) : null;
        if (!hit) {
          return;
        }
        return hit?.objectID;
      })
      .filter((val) => Boolean(val)) as string[];
  } catch (err) {
    console.error(
      'Error filtering enabled (product configurator) straps:',
      err,
    );
    return bundleVariationSkus;
  }
};

//SSR ONLY
export const searchWatchesBySku = async (sku: string, ctxLocale: string) => {
  const index = algoliaSsrSearchClient.initIndex(
    getIndexName('PRODUCT', ctxLocale),
  );
  return index.search<WatchHit>(`${sku}`, {
    filters: `product_type:Watch`,
    queryType: 'prefixNone',
  });
};

export const getDesignerChoiceByPartialSku = async (
  sku: string,
  ctxLocale: string,
) => {
  const data = await searchWatchesBySku(sku, ctxLocale);
  const len = data.hits.length;
  let result = null;
  if (len > 0) {
    for (let i = 0; i < len; i++) {
      const item = data.hits[i];
      if (item.designer_choice && item.objectID?.includes(sku)) {
        result = item;
        break;
      }
    }
  }
  return result;
};

export const parseUrlToRouteState = (url: string): Record<string, any> => {
  const params = new URLSearchParams(url.split('?')[1]);
  const routeState: Record<string, any> = {};

  params.forEach((value, key) => {
    if (routeState[key]) {
      if (Array.isArray(routeState[key])) {
        routeState[key].push(value);
      } else {
        routeState[key] = [routeState[key], value];
      }
    } else {
      routeState[key] = value;
    }
  });

  Object.keys(routeState).forEach((key) => {
    if (!Array.isArray(routeState[key])) {
      if (key !== 'q') {
        routeState[key] = [routeState[key]];
      }
    }
  });

  return routeState;
};

function filterReleasedOrDraftProducts(
  hits: AlgoliaHit[],
  filterType: ProductConfigurationFilterType,
  productType?: ProductType,
  zeroTime: boolean | undefined = false,
  draftMode: boolean | undefined = false,
) {
  const currentUnixTimestamp = zeroTime
    ? Math.floor(dayjs().utc().startOf('date').toDate().getTime() / 1000)
    : Math.floor(new Date().getTime() / 1000);

  const results = hits.filter((hit) => {
    if (!hit || (productType && hit.product_type !== productType)) {
      return false;
    }

    const isReleased =
      `${filterType}_release_ts` in hit &&
      hit[`${filterType}_release_ts`] < currentUnixTimestamp &&
      `${filterType}_expire_ts` in hit &&
      hit[`${filterType}_expire_ts`] > currentUnixTimestamp;

    const isPreview = draftMode && hit[`${filterType}_preview`];

    return isReleased || isPreview;
  });
  return results;
}

export const getFilterReleasedProducts = async <T extends ProductHit>(
  skus: string[],
  locale: string,
  productType?: ProductType,
  filterType = ProductConfigurationFilterType.PLP,
  indexName?: string,
  isSsr = false,
  draftMode: boolean | undefined = false,
) => {
  const _indexName = indexName ?? getIndexName('PRODUCT', locale);
  const client: SearchClient = isSsr
    ? algoliaSsrSearchClient
    : algoliaSearchClient;
  const index = client.initIndex(_indexName);

  const hits = (await index.getObjects<T>(skus)).results.filter(
    (item) => !!item,
  ) as T[];

  return filterReleasedOrDraftProducts(
    hits,
    filterType,
    productType,
    false,
    draftMode,
  ) as T[];
};

export async function isWatchEnabledInProductConfigurator(
  locale: string | undefined,
  draftMode: undefined | boolean,
  sku: string,
) {
  const indexName = getIndexName(
    'PRODUCT',
    locale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  );

  const algoliaFilters = applyProductConfigurationFilters(
    ProductConfigurationFilterType.PDP,
    'Watch',
    draftMode,
    `objectID:${sku}`,
  );
  const index = algoliaSsrSearchClient.initIndex(indexName);

  const { nbHits } = await index.search('', {
    filters: algoliaFilters,
  });

  return nbHits > 0;
}

export async function getProduct<T extends WatchHit | StrapHit | EyewearHit>(
  locale: string | undefined,
  sku: string,
  client: SearchClient,
): Promise<T | null> {
  const indexName = getIndexName(
    'PRODUCT',
    locale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  );

  const index = client.initIndex(indexName);

  try {
    return await index.getObject<T>(sku);
  } catch (error) {
    return null;
  }
}

export async function getProducts<T extends WatchHit | StrapHit | EyewearHit>(
  locale: string | undefined,
  skus: string[],
  client: SearchClient,
): Promise<(T | null)[]> {
  const indexName = getIndexName(
    'PRODUCT',
    locale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  );
  const index = client.initIndex(indexName);
  const response = await index.getObjects<T>(skus);
  return response.results;
}

export function checkTimestamps(
  expireTs: number | null | undefined,
  releaseTs: number | null | undefined,
  now: number = Math.floor(new Date().getTime() / 1000),
) {
  if (expireTs && expireTs <= now) return false;
  if (releaseTs && releaseTs >= now) return false;
  if (expireTs && releaseTs && expireTs < releaseTs) return false;
  return true;
}

export function checkProductActivation(
  hit: WatchHit | StrapHit | EyewearHit | undefined | null,
  draftMode: undefined | boolean,
) {
  const useProductConfigurator =
    process.env.NEXT_PUBLIC_USE_PRODUCT_CONFIGURATOR === 'true';

  if (!hit) {
    return {
      isPdpActivated: false,
      isPlpActivated: false,
      isSearchActivated: false,
    };
  }

  if (!useProductConfigurator) {
    return {
      isPdpActivated: true,
      isPlpActivated: true,
      isSearchActivated: true,
    };
  }

  return {
    isPdpActivated:
      checkTimestamps(hit.pdp_expire_ts, hit.pdp_release_ts) ||
      (draftMode ? hit.pdp_preview : false),
    isPlpActivated:
      checkTimestamps(hit.plp_expire_ts, hit.plp_release_ts) ||
      (draftMode ? hit.plp_preview : false),
    isSearchActivated:
      checkTimestamps(hit.search_expire_ts, hit.search_release_ts) ||
      (draftMode ? hit.search_preview : false),
  };
}

export const sendAlgoliaEvent = (algoliaEvent: AlgoliaEvent) => {
  switch (algoliaEvent.event) {
    case AlgoliaEventsNames.clickedObjectIDsAfterSearch:
      aa(algoliaEvent.event, {
        index: algoliaEvent.indexName ?? '',
        eventName: ALGOLIA_EVENT_NAME.productClicked,
        objectIDs: algoliaEvent.objectIDs ?? [],
        queryID: algoliaEvent.queryID ?? '',
        positions: algoliaEvent.positions ?? [],
      });
      break;
    case AlgoliaEventsNames.convertedObjectIDsAfterSearch:
      aa(algoliaEvent.event, {
        index: algoliaEvent.indexName ?? '',
        eventName: ALGOLIA_EVENT_NAME.productConverted,
        objectIDs: algoliaEvent.objectIDs ?? [],
        queryID: algoliaEvent.queryID ?? '',
      });
      break;
    case AlgoliaEventsNames.addedToCartObjectIDsAfterSearch:
      aa(algoliaEvent.event, {
        index: algoliaEvent.indexName ?? '',
        eventName: ALGOLIA_EVENT_NAME.productAddedToCart,
        objectIDs: algoliaEvent.objectIDs ?? [],
        queryID: algoliaEvent.queryID ?? '',
        value: algoliaEvent.value,
        currency: algoliaEvent.currency,
      });
      break;
    case AlgoliaEventsNames.purchasedObjectIDsAfterSearch:
      aa(algoliaEvent.event, {
        index: algoliaEvent.indexName ?? '',
        eventName: ALGOLIA_EVENT_NAME.productPurchased,
        objectIDs: algoliaEvent.objectIDs ?? [],
        queryID: algoliaEvent.queryID ?? '',
        value: algoliaEvent.value,
        currency: algoliaEvent.currency,
      });
      break;
  }
};
